import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, SpinningLoader } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import AddNewDxModal from "../../../../components/modals/addNewDxModal/addNewDxModal";
import EditDxCodeSidePanel from "../../../../components/modals/editDxCodeSidePanel/editDxCodeSidePanel";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { DxCodeConfigTab } from "../../../../../shared/enums/dxCodeConfigTab";
import { fetchHealthFacilityFromFirebase } from "../../../../redux/slice/healthFacilitySlice";
import { fetchNccnIcdConfigFromFirebase } from "../../../../redux/slice/nccnIcdConfigSlice";
import { fetchNccnRegimenConfigFromFirebase } from "../../../../redux/slice/nccnRegimenConfigSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { logError } from "../../../../utils/customLogger";
import { dxCodeConfigTableHeaders } from "./dxCodeConfigTable";
import { useEditDxCodeSave } from "./hooks/useEditDxCodeSave";
import { useEditingConfig } from "./hooks/useEditingConfig";
import { useTableData } from "./hooks/useTableData";
import { regimenConfigTableHeaders } from "./regimenConfigTable";
import { createConfig, CreateConfigData } from "./utils/dxCodeConfigUtils";

const DxCodeConfig = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: nccnIcdConfigData, loading: nccnIcdConfigLoading } =
    useSelector((state: RootState) => state.nccnIcdConfig);
  const { data: nccnRegimenConfigData, loading: nccnRegimenConfigLoading } =
    useSelector((state: RootState) => state.nccnRegimenConfig);
  const { data: healthFacilityData, loading: healthFacilityLoading } =
    useSelector((state: RootState) => state.healthFacility);
  const [activeTab, setActiveTab] = useState<DxCodeConfigTab>(
    DxCodeConfigTab.DRUG,
  );
  const [isAddDrugModalOpen, setIsAddDrugModalOpen] = useState(false);
  const [isEditDxCodeModalOpen, setIsEditDxCodeModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [editingRowData, setEditingRowData] = useState<any>(null);

  const tabs = [
    { id: DxCodeConfigTab.REGIMEN, label: "Regimen" },
    { id: DxCodeConfigTab.DRUG, label: "Drug" },
  ];

  useEffect(() => {
    dispatch(fetchNccnIcdConfigFromFirebase());
    dispatch(fetchNccnRegimenConfigFromFirebase());
    dispatch(fetchHealthFacilityFromFirebase());
  }, [dispatch]);

  // Reset search term when switching tabs
  useEffect(() => {
    setSearchTerm("");
  }, [activeTab]);

  const organizationOptions = useMemo(() => {
    if (!healthFacilityData?.length) return [];

    const uniqueOptions = new Map<string, { label: string; value: string }>();

    healthFacilityData.forEach((facility) => {
      const value = facility.DocId?.trim();
      if (!value || uniqueOptions.has(value)) return;

      uniqueOptions.set(value, {
        label: facility.Name ?? "",
        value,
      });
    });

    return Array.from(uniqueOptions.values());
  }, [healthFacilityData]);

  const handleOrganizationChange = (option: { value: string }) => {
    setSelectedOrgId(option.value);
  };

  // Use custom hook for table data transformation and filtering
  const { drugTableData, regimenTableData, tableData } = useTableData(
    activeTab,
    nccnIcdConfigData,
    nccnRegimenConfigData,
    searchTerm,
  );
  const tableHeaders =
    activeTab === DxCodeConfigTab.REGIMEN
      ? regimenConfigTableHeaders
      : dxCodeConfigTableHeaders;
  const isLoading =
    activeTab === DxCodeConfigTab.REGIMEN
      ? nccnRegimenConfigLoading
      : nccnIcdConfigLoading;

  const handleAddDrug = () => {
    setIsAddDrugModalOpen(true);
  };

  const handleCloseAddDrugModal = () => {
    setIsAddDrugModalOpen(false);
  };

  const handleAddDrugSubmit = async (data: CreateConfigData) => {
    setIsSaving(true);
    try {
      await createConfig(dispatch, activeTab, data);
      toast.success(ToastMessages.DX_CODE_CONFIG_CREATE_SUCCESS);
      handleCloseAddDrugModal();
    } catch (error) {
      logError(error as Error, "DxCodeConfig - handleAddDrugSubmit");
      const isSelectPreviousValidation =
        error instanceof Error &&
        error.message === ToastMessages.DX_CODE_CONFIG_SELECT_PREVIOUS_REQUIRED;
      toast.error(
        isSelectPreviousValidation
          ? error.message
          : ToastMessages.DX_CODE_CONFIG_CREATE_ERROR,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditIconClick = (rowData: any) => {
    setEditingRowData(rowData);
    setIsEditDxCodeModalOpen(true);
  };

  const handleCloseEditDxCodeModal = () => {
    setIsEditDxCodeModalOpen(false);
    setEditingRowData(null);
  };

  const handleEditDxCodeSave = useEditDxCodeSave({
    activeTab,
    setIsSaving,
    onClose: handleCloseEditDxCodeModal,
  });

  // Use custom hook for editing config data
  const editingConfigData = useEditingConfig(
    editingRowData,
    selectedOrgId,
    activeTab,
    nccnIcdConfigData,
    nccnRegimenConfigData,
  );

  return (
    <div className="dx-code-config flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="dx-code-config__header flex items-center justify-between">
        <div className="px-2 text-lg font-bold leading-normal text-black">
          DX Code Config
        </div>
        <div className="flex w-[30%] justify-end">
          {healthFacilityLoading ? (
            <div className="flex h-10 items-center gap-3 rounded-md border border-primaryGray-14 bg-white px-3 shadow-sm">
              <span className="flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">
                <SpinningLoader />
              </span>
              <span className="truncate text-sm text-primaryGray-6">
                Loading organizations…
              </span>
            </div>
          ) : (
            <div className="w-full max-w-sm">
              <Select
                id="organization"
                options={organizationOptions}
                label=""
                placeholder="Select Organization"
                onOptionChange={handleOrganizationChange}
                defaultValue={selectedOrgId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      {selectedOrgId && (
        <div className="mb-2 flex border-b border-primaryGray-14">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-tertiaryBlue-4 bg-tertiaryBlue-13 font-bold text-tertiaryBlue-4"
                  : "text-primaryGray-6"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content Area */}
      <div className="dx-code-config__content flex min-h-0 w-full flex-1 flex-col">
        {!selectedOrgId ? (
          <div className="flex h-40 items-center justify-center text-center text-sm text-gray-500">
            Please select an organization to view and manage DX code
            configurations.
          </div>
        ) : (
          <div className="dx-code-config__table-container flex min-h-0 flex-1 flex-col p-4">
            {/* Table */}
            <div className="dx-code-config__table flex min-h-0 flex-1 flex-col overflow-y-auto">
              <CustomTable
                tableHeaders={tableHeaders}
                tableData={tableData}
                tableName="dxCodeConfig"
                itemsPerPage={15}
                pagesPerView={4}
                count={tableData.length}
                totalCount={tableData.length}
                hideSearchBar={false}
                placeholder={
                  activeTab === DxCodeConfigTab.REGIMEN
                    ? "Search by Regimen Code, Regimen Name"
                    : "Search by J code, Drug Name"
                }
                searchingText={(searchText: string) => {
                  setSearchTerm(searchText);
                }}
                isFetching={isLoading}
                showExportButton={false}
                showRefreshButton={false}
                showBulkAssignButton={false}
                bulkAssignButtonDisabled={false}
                onBulkAssignButtonClick={(e) => {
                  e.preventDefault();
                }}
                showOtherButton={true}
                otherButtonMeta={{
                  text:
                    activeTab === DxCodeConfigTab.REGIMEN
                      ? "Add Regimen"
                      : "Add Drug",
                  onClick: (e) => {
                    e.preventDefault();
                    handleAddDrug();
                  },
                  disabled: false,
                  buttonType: "primary",
                  size: "medium",
                }}
                onEditIconClick={handleEditIconClick}
              />
            </div>
          </div>
        )}
      </div>

      {/* Add New DX Modal */}
      <AddNewDxModal
        isOpen={isAddDrugModalOpen}
        onClose={handleCloseAddDrugModal}
        onSubmit={handleAddDrugSubmit}
        mode={activeTab === DxCodeConfigTab.REGIMEN ? "regimen" : "drug"}
        drugOptions={
          activeTab === DxCodeConfigTab.DRUG
            ? drugTableData.map((drug) => drug.actions)
            : []
        }
        regimenOptions={
          activeTab === DxCodeConfigTab.REGIMEN
            ? regimenTableData.map((regimen) => ({
                id: regimen.id,
                regimenCode: regimen.regimenCode,
                regimenName: regimen.regimenName,
              }))
            : []
        }
        loading={isSaving}
      />

      {/* Edit DX Code Side Panel */}
      {editingConfigData && (
        <EditDxCodeSidePanel
          isOpen={isEditDxCodeModalOpen}
          onClose={handleCloseEditDxCodeModal}
          onSave={handleEditDxCodeSave}
          mode={editingConfigData.mode}
          drugName={editingConfigData.drugName}
          jCode={editingConfigData.jCode}
          drugType={editingConfigData.drugType}
          regimenName={editingConfigData.regimenName}
          regimenCode={editingConfigData.regimenCode}
          dxCodes={editingConfigData.dxCodes}
          selectedOrgId={selectedOrgId}
          isSaving={isSaving}
          hasPriorityConfig={editingConfigData.hasPriorityConfig}
        />
      )}
    </div>
  );
};

export default DxCodeConfig;
